//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, IconButton, ListItemText, Menu, MenuItem, Paper, Tooltip
} from '@mui/material';
import {
  useTheme, type SxProps, type Theme
} from '@mui/material/styles';
import {
  ResizableBox, type ResizeCallbackData, type ResizeHandleAxis
} from 'react-resizable';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  clampSize, resizeCeiling, resizeFrom, resolveAnchor, PANEL_RESIZE_HANDLES,
  type PanelBounds, type PanelRect, type PanelResizeHandle
} from './panelGeometry';
import PanelIcon from './PanelIcon';
import BrandedTitleBar from '@/components/Elements/BrandedTitleBar';
import { panelElementId } from './panelIds';
import { type PanelEntry } from '@/contexts/PanelContext';
import { PANEL_ANCHORS, type PanelAnchor } from '@/services/apiTypes';

interface FloatingPanelProps {
  entry: PanelEntry;
  bounds: PanelBounds;
  zIndex: number;
  onMove: (x: number, y: number) => void;
  // A rect, not a size: dragging the north or west edge moves the origin as
  // well, and the two are one gesture.
  onResize: (rect: PanelRect) => void;
  onIconify: () => void;
  onRaise: () => void;
  onResetGeometry: () => void;
}

interface DragOrigin {
  // Which pointer the drag belongs to. A title bar can be under two fingers
  // at once, and every event carries the identity of the finger that made it:
  // without matching on it, the second finger's press overwrites this origin
  // and the first finger's next move teleports the panel by the distance
  // between the two contacts.
  pointerId: number;
  pointerX: number;
  pointerY: number;
  panelX: number;
  panelY: number;
}

// Human-readable anchor names, for the menu and for accessible names.
const ANCHOR_LABELS: Record<PanelAnchor, string> = {
  'top-left': 'Top left',
  'top-right': 'Top right',
  'center-left': 'Center left',
  'center-right': 'Center right',
  'bottom-left': 'Bottom left',
  'bottom-right': 'Bottom right'
};

// Size presets, as fractions of the available region. The keyboard equivalent
// of dragging the resize handle.
const SIZE_PRESETS: ReadonlyArray<{ label: string; fraction: number }> = [
  { label: 'Small', fraction: 0.3 },
  { label: 'Medium', fraction: 0.5 },
  { label: 'Large', fraction: 0.75 }
];

// An edge strip straddles the border, half its width outside the frame. Thin
// on purpose: the cursor is the affordance, and a strip wide enough to see
// would eat into the panel's own content.
const EDGE_SPAN = 8;
const EDGE_OFFSET = -EDGE_SPAN / 2;

// Corners are squares centered on the ends of the strips, large enough to
// aim at and stacked above them so a corner never yields the edge running
// through it. `zIndex` is what does that: the strips span the full side, so
// every corner overlaps two of them.
//
// Centered is what the offset buys: half the square outside the frame, half
// inside. Any less of it outside and the rest is painted over the body,
// where it takes presses meant for whatever the client drew there.
const CORNER_SPAN = 20;
const CORNER_OFFSET = -CORNER_SPAN / 2;

// Where each handle sits, and what the pointer becomes over it. The
// double-headed diagonals are what a desktop window shows on its corners.
const HANDLE_PLACEMENT: Record<PanelResizeHandle, SxProps<Theme>> = {
  n: { top: EDGE_OFFSET, left: 0, right: 0, height: EDGE_SPAN,
       cursor: 'ns-resize' },
  s: { bottom: EDGE_OFFSET, left: 0, right: 0, height: EDGE_SPAN,
       cursor: 'ns-resize' },
  e: { right: EDGE_OFFSET, top: 0, bottom: 0, width: EDGE_SPAN,
       cursor: 'ew-resize' },
  w: { left: EDGE_OFFSET, top: 0, bottom: 0, width: EDGE_SPAN,
       cursor: 'ew-resize' },
  nw: { top: CORNER_OFFSET, left: CORNER_OFFSET, width: CORNER_SPAN,
        height: CORNER_SPAN, cursor: 'nwse-resize', zIndex: 1 },
  ne: { top: CORNER_OFFSET, right: CORNER_OFFSET, width: CORNER_SPAN,
        height: CORNER_SPAN, cursor: 'nesw-resize', zIndex: 1 },
  sw: { bottom: CORNER_OFFSET, left: CORNER_OFFSET, width: CORNER_SPAN,
        height: CORNER_SPAN, cursor: 'nesw-resize', zIndex: 1 },
  se: { bottom: CORNER_OFFSET, right: CORNER_OFFSET, width: CORNER_SPAN,
        height: CORNER_SPAN, cursor: 'nwse-resize', zIndex: 1 }
};

/**
 * One floating panel: the host owns this chrome, the client owns the body.
 *
 * A non-modal dialog rather than a modal one, because the application behind
 * it stays live by design -- that is the whole reason a panel exists instead
 * of a modal.  So focus moves in on open but is never trapped, and Escape
 * iconifies rather than closing: closing unmounts, and a reflexive keypress
 * must not be able to discard what a plugin is holding.  A body may take
 * Escape for itself by stopping its propagation, as the chat panel does
 * while a turn is running, when Escape is its stop key.
 *
 * The one exception: while the options menu is open, MUI's `Modal` behind it
 * marks the panel and the rest of the application `aria-hidden`, so for that
 * moment assistive technology sees only the menu.  The suspension lasts no
 * longer than the menu does -- focus is inside it and Escape dismisses it --
 * and the menu is this feature's whole keyboard path to moving and resizing a
 * panel, which is why it keeps `Menu`'s proven focus handling instead of a
 * hand-rolled popper that would preserve the invariant.
 */
const FloatingPanel = ({
  entry, bounds, zIndex, onMove, onResize, onIconify, onRaise,
  onResetGeometry
}: FloatingPanelProps) => {
  const theme = useTheme();
  const { spec, rect, state, takesFocus, focusEpoch } = entry;
  const isOpen = state === 'open';
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragOrigin | null>(null);
  const [dragDelta, setDragDelta] = useState({ x: 0, y: 0 });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Which edge the live resize is pulling, and where that puts the panel.
  // The library reports a size and moves nothing, so the origin a north or
  // west drag implies has to be previewed here or the panel appears to grow
  // away from the edge under the pointer.
  const [handle, setHandle] = useState<PanelResizeHandle>('se');
  const [resizePreview, setResizePreview] = useState<PanelRect | null>(null);

  // Focus the panel when the user opens it, so a keyboard user lands inside
  // what they just asked for and Escape has somewhere to be heard. A panel
  // the session restored is put back without this: nobody asked for it, and
  // seizing focus on page load would land it in a portal at the end of the
  // body, past the skip links, with nothing having happened.
  //
  // The container is the fallback, not the rule: a body with somewhere better
  // to put focus -- chat's message box -- has already put it there by the
  // time this runs, because React runs a child's effects before its parent's.
  //
  // The epoch is a dependency because opening a panel that is already open
  // changes neither of the other two: without it, the second request would
  // move focus nowhere and the user who made it would be left wherever they
  // clicked from.
  useEffect(() => {
    if (!isOpen || !takesFocus) return;
    const container = containerRef.current;
    if (container && !container.contains(document.activeElement))
      container.focus();
  }, [isOpen, takesFocus, focusEpoch]);

  // The menu is portaled and its anchor is the title bar, which an iconified
  // panel no longer displays: left open it would hang there over nothing,
  // anchored to an element MUI cannot position against. A resize preview
  // goes for the same reason a canceled drag's does: it is a transform that
  // would survive the round trip and put the title bar out of reach.
  useEffect(() => {
    if (!isOpen) {
      setMenuAnchor(null);
      setResizePreview(null);
    }
  }, [isOpen]);

  const titleId = `panel-title-${spec.id}`;
  const menuButtonId = `panel-menu-button-${spec.id}`;

  const handleDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    // One drag at a time: a second pointer landing on the bar mid-drag is a
    // stray touch, not a new gesture, and admitting it would swap the origin
    // out from under the finger that is dragging.
    if (dragRef.current) return;
    // Capturing the pointer makes the browser retarget this pointer's
    // compatibility mouse events -- click included -- to the capture element,
    // so anything interactive inside the title bar would never see its own
    // click. An allowlist rather than a list of control types to avoid, so
    // whatever the title bar grows next is inert here by construction.
    const from = event.target;
    const isDragSurface = from === event.currentTarget ||
      (from instanceof Element && from.id === titleId);
    if (!isDragSurface) return;
    dragRef.current = {
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      panelX: rect.x,
      panelY: rect.y
    };
    // jsdom does not implement pointer capture, and a test that dispatches a
    // drag must not die on it.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDragMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragRef.current;
    if (!origin || origin.pointerId !== event.pointerId) return;
    setDragDelta({
      x: event.clientX - origin.pointerX,
      y: event.clientY - origin.pointerY
    });
  };

  const handleDragEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragRef.current;
    if (!origin || origin.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragDelta({ x: 0, y: 0 });
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const dx = event.clientX - origin.pointerX;
    const dy = event.clientY - origin.pointerY;
    // A click on the title bar is a press and a release at one point, and it
    // moved the panel nowhere. Committing it would write the rect the panel
    // currently has -- which may be one the bounds forced on it -- as a
    // position the user chose, and the registry keeps such a rect out of
    // storage precisely so a narrow window does not become tomorrow's
    // remembered place. Exact zero rather than a threshold: the browser
    // reports the same point for a click, so nothing wider is needed, and a
    // threshold would swallow the smallest real nudges.
    if (dx === 0 && dy === 0) return;
    onMove(origin.panelX + dx, origin.panelY + dy);
  };

  // An interrupted gesture is not a completed intent, so the preview is
  // discarded rather than committed wherever the pointer happened to be.
  //
  // Without this the delta stays frozen, and it is component state that
  // survives an iconify and a restore: the panel keeps a preview transform
  // that puts the title bar you would grab to fix it off screen, and only a
  // reload gets it back.
  const handleDragCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragRef.current;
    if (!origin || origin.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragDelta({ x: 0, y: 0 });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    onIconify();
  };

  const handleAnchor = (anchor: PanelAnchor) => {
    setMenuAnchor(null);
    const at = resolveAnchor(anchor, { w: rect.w, h: rect.h }, bounds);
    onMove(at.x, at.y);
  };

  const handlePreset = (fraction: number) => {
    setMenuAnchor(null);
    onResize({
      x: rect.x,
      y: rect.y,
      w: Math.round(bounds.width * fraction),
      h: Math.round(bounds.height * fraction)
    });
  };

  // The registry's own floor, so a drag cannot shrink the panel below what
  // will be committed and snap back. A spec's minimum is optional.
  const floor = clampSize({ w: 0, h: 0 }, spec.minSize, bounds);

  const resizedRect = (data: ResizeCallbackData): PanelRect => resizeFrom(
    data.handle as PanelResizeHandle, rect,
    { w: data.size.width, h: data.size.height }, spec.minSize, bounds);

  // How far the origin has traveled during a live resize. Zero for a south
  // or east drag, which pins the origin, and zero when nothing is dragging.
  const previewOffset = resizePreview ?
    { x: resizePreview.x - rect.x, y: resizePreview.y - rect.y } :
    { x: 0, y: 0 };

  // One body element per spec, so the chrome's own re-renders -- one per
  // pointer move during a drag or live resize -- hand React the element it
  // already has and the whole client subtree is skipped. Calling
  // `spec.render()` inline instead would re-render the body (a chat
  // transcript, a plugin frame's wrapper) at pointer rate.
  const body = useMemo(() => spec.render(), [spec]);

  return (
    <>
      <ResizableBox
        width={rect.w}
        height={rect.h}
        minConstraints={[floor.w, floor.h]}
        // Per handle, because the room a drag has depends on which edge it
        // pins: the ceiling for a west drag is measured to the left of the
        // panel, not to the right. Set from the handle the gesture started
        // on, so what the pointer draws is already inside the region rather
        // than snapping back on release.
        maxConstraints={resizeCeiling(handle, rect, bounds)}
        resizeHandles={
          spec.isResizable ? [...PANEL_RESIZE_HANDLES] : []}
        axis="both"
        handleSize={[10, 10]}
        lockAspectRatio={false}
        transformScale={1}
        // Invisible hit targets: react-resizable's own handles take their
        // size, position and cursor from a stylesheet this application does
        // not import, leaving zero-size spans with nothing to aim at. The
        // ref is how the library attaches the drag, so it has to reach the
        // element.
        //
        // Nothing is drawn on any of the eight -- the cursor over a border
        // is the whole affordance, as it is on a desktop window. They are
        // `aria-hidden` for the same reason a cursor is: pointer-only, with
        // the size presets in the panel's own menu as the path that needs no
        // pointer.
        handle={(
          axis: ResizeHandleAxis, ref: React.RefObject<HTMLElement | null>
        ) => (
          <Box
            aria-hidden
            data-testid={`panel-resize-handle-${spec.id}-${axis}`}
            ref={(node: HTMLDivElement | null) => { ref.current = node; }}
            sx={{
              position: 'absolute',
              touchAction: 'none',
              ...HANDLE_PLACEMENT[axis as PanelResizeHandle]
            }}
          />
        )}
        // Raise on the way in: a handle sits outside the Paper, so a resize
        // of a buried panel would otherwise leave it buried.
        onResizeStart={(
          _event: React.SyntheticEvent, data: ResizeCallbackData
        ) => {
          setHandle(data.handle as PanelResizeHandle);
          onRaise();
        }}
        onResize={(
          _event: React.SyntheticEvent, data: ResizeCallbackData
        ) => setResizePreview(resizedRect(data))}
        onResizeStop={(
          _event: React.SyntheticEvent, data: ResizeCallbackData
        ) => {
          setResizePreview(null);
          onResize(resizedRect(data));
        }}
        style={{
          position: 'fixed',
          // The drag is previewed with a transform so the committed rect
          // changes once, on release, rather than on every pointer move.
          left: rect.x,
          top: rect.y,
          transform: `translate(${dragDelta.x + previewOffset.x}px, ` +
            `${dragDelta.y + previewOffset.y}px)`,
          display: isOpen ? 'flex' : 'none',
          flexDirection: 'column',
          zIndex
        }}
      >
        <Paper
          ref={containerRef}
          id={panelElementId(spec.id)}
          elevation={6}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          onPointerDown={onRaise}
          sx={{
            width: '100%',
            height: '100%',
            // No global border-box rule here, and the border would otherwise
            // paint outside the rect the clamp is computed on.
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: theme.border.default,
            borderRadius: theme.border.radius
          }}
        >
          <BrandedTitleBar
            data-testid={`panel-drag-handle-${spec.id}`}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragCancel}
            onLostPointerCapture={handleDragCancel}
            titleId={titleId}
            title={spec.title}
            variant="subtitle2"
            icon={spec.icon && <PanelIcon icon={spec.icon} />}
            sx={{
              px: 1,
              py: 0.5,
              cursor: 'move',
              touchAction: 'none',
              userSelect: 'none',
              // The app header's own chrome colors, so a floating window
              // reads as chrome rather than as another patch of the page it
              // floats over. The contrast against the body is also the
              // separation a divider used to draw here.
              backgroundColor: theme.palette.menuPanel.default.background,
              color: theme.palette.menuPanel.default.text
            }}
            actions={(
              <>
                {/* Both controls inherit the bar's text color: their default
                    dark gray would vanish against the chrome. */}
                <Tooltip describeChild title="Panel options">
                  <IconButton
                    size="small"
                    color="inherit"
                    id={menuButtonId}
                    aria-label={`${spec.title} panel options`}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(menuAnchor)}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* One dismissal, and it never discards: the X parks the
                    panel with its state intact, because an X is the glyph
                    everyone reads as "make this window go away". Stopping a
                    tool -- releasing its frame and whatever it holds --
                    lives in the Tools list, where it can be named for what
                    it does and asked about first. Two controls up here that
                    both made the window go away was the thing nobody could
                    tell apart. */}
                <Tooltip describeChild title="Close">
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label={`Close ${spec.title}`}
                    onClick={onIconify}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          />
          <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
            {body}
          </Box>
        </Paper>
      </ResizableBox>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        // A panel exists so the application behind it stays live, and
        // Popover locks the page scroll unless told not to.
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { dense: true, 'aria-labelledby': menuButtonId } }}
        sx={{ zIndex: (t) => t.zIndex.tooltip }}
      >
        {PANEL_ANCHORS.map((anchor) => (
          <MenuItem key={anchor} onClick={() => handleAnchor(anchor)}>
            <ListItemText>
              Move to {ANCHOR_LABELS[anchor].toLowerCase()}
            </ListItemText>
          </MenuItem>
        ))}
        {spec.isResizable && SIZE_PRESETS.map((preset) => (
          <MenuItem
            key={preset.label}
            onClick={() => handlePreset(preset.fraction)}
          >
            <ListItemText>Size {preset.label.toLowerCase()}</ListItemText>
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onResetGeometry();
          }}
        >
          <ListItemText>Reset position and size</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default FloatingPanel;
